import { api } from './api';

export interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  expiresAt: string;
  user: UserDto;
}

export interface UserDto {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  createdAt: string;
  role?: string; // 'teacher', 'student', 'superadmin'
  tenantId?: string | null; // For superadmin, this might be null
}

// Normalize user object coming from API/cookies (which may use PascalCase) into our UserDto shape
const normalizeUser = (raw: any): UserDto => {
  if (!raw) {
    throw new Error('Cannot normalize empty user');
  }

  // Support both camelCase (frontend) and PascalCase (backend) property names
  const id = raw.id ?? raw.Id;
  const email = raw.email ?? raw.Email;
  const firstName = raw.firstName ?? raw.FirstName ?? null;
  const lastName = raw.lastName ?? raw.LastName ?? null;
  const avatarUrl = raw.avatarUrl ?? raw.AvatarUrl ?? null;
  const role = raw.role ?? raw.Role;
  const tenantId = raw.tenantId ?? raw.TenantId ?? null;
  const createdAt = raw.createdAt ?? raw.CreatedAt;

  return {
    id,
    email,
    firstName,
    lastName,
    avatarUrl,
    role,
    tenantId,
    createdAt,
  };
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  const value = match.split('=')[1];
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const authService = {
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register', data);
    const normalizedUser = normalizeUser((response.data as any).user);
    return {
      ...response.data,
      user: normalizedUser,
    };
  },

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', data);
    const normalizedUser = normalizeUser((response.data as any).user);
    return {
      ...response.data,
      user: normalizedUser,
    };
  },

  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email: email.trim() });
  },

  async completePasswordReset(accessToken: string, newPassword: string): Promise<void> {
    await api.post('/auth/reset-password', {
      accessToken,
      newPassword,
    });
  },

  async getCurrentUser(): Promise<UserDto> {
    // Debug: log all cookies
    console.log('[WebApp authService] getCurrentUser: all cookies', document.cookie);
    
    // For the marketing site we rely entirely on the shared public cookie
    // set by the API (arrangelly_user). If it's not present or invalid, we
    // treat the user as unauthenticated and DO NOT call /auth/me here.
    try {
      const userCookie = getCookie('arrangelly_user');
      console.log(
        '[WebApp authService] getCurrentUser: user cookie raw value?',
        !!userCookie,
        userCookie?.substring(0, 100)
      );
      if (userCookie) {
        console.log('[WebApp authService] getCurrentUser: found user cookie, parsing...');
        const parsedRaw = JSON.parse(userCookie);
        const parsed = normalizeUser(parsedRaw);
        console.log(
          '[WebApp authService] getCurrentUser: parsed & normalized user from cookie',
          JSON.stringify(parsed, null, 2)
        );
        return parsed;
      }

      console.log(
        '[WebApp authService] getCurrentUser: no user cookie found, treating as unauthenticated (will not call /auth/me)'
      );
      throw new Error('No webapp auth cookie');
    } catch (e) {
      console.log(
        '[WebApp authService] getCurrentUser: failed to parse user cookie, treating as unauthenticated',
        e
      );
      throw e;
    }
  },

  async logout() {
    // Tell API to clear all auth cookies (arrangelly_token, arrangelly_user, arrangelly_expires_at)
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error during logout API call:', error);
    }
    // From webapp (arrangelly.local), just refresh the current page
    window.location.href = '/';
  },

  getToken(): string | null {
    // For debugging/visibility only: read non-HttpOnly token cookie if present
    const token = getCookie('arrangelly_token');
    console.log('[WebApp authService] getToken: token from cookie?', !!token);
    return token;
  },

  getUser(): UserDto | null {
    try {
      const userCookie = getCookie('arrangelly_user');
      if (!userCookie) return null;
      const parsedRaw = JSON.parse(userCookie);
      const parsed = normalizeUser(parsedRaw);
      return parsed;
    } catch {
      return null;
    }
  },

  setAuth(_authResponse: AuthResponse) {
    // API already set the shared auth cookie (arrangelly_token); nothing to store on client
  },
};

